import React, { useState, useEffect } from "react";
import { Alert } from "@mui/material";
import { FaTimes, FaCoffee, FaPlus, FaSearch } from "react-icons/fa";
import { useCreateEspressoDeviceMutation } from "../../store/storageRoles/devicesApi";
import useAuth from "../../Hooks/useAuth";
import useDrawerTransition from "../../Hooks/useDrawerTransition";

// Espresso makinesinde kota yalnız "Espresso" ürünü üzerinden tutulur (Server#221).
const ESPRESSO_PRODUCTS = ["Espresso"];

export default function CreateDeviceModal({ open, onClose, onSuccess }) {
  // Kapalı pozisyonda mount → kayarak açılır; kapanışta çıkış animasyonu
  // bitene kadar monte kalır.
  const { mounted, entered } = useDrawerTransition(open);
  const { auth } = useAuth();
  const [serialNo, setSerialNo] = useState("");
  const [searchSerialNo, setSearchSerialNo] = useState("");
  const [cupNumber, setCupNumber] = useState(1);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showSerialInput, setShowSerialInput] = useState(false);
  const [created, setCreated] = useState(false);

  const [createEspressoDevice, { isLoading, isError, error: apiError, reset: resetMutation }] =
    useCreateEspressoDeviceMutation();

  useEffect(() => {
    if (isError && apiError) {
      const errorMessage =
        apiError?.data?.message ||
        apiError?.data?.error ||
        "Cihaz oluşturulurken bir hata oluştu";
      setError(errorMessage);
      setSuccess("");
    }
  }, [isError, apiError]);

  const handleClose = () => {
    if (created) {
      onSuccess?.();
    }
    setSerialNo("");
    setSearchSerialNo("");
    setCupNumber(1);
    setError("");
    setSuccess("");
    setShowSerialInput(false);
    setCreated(false);
    resetMutation();
    onClose();
  };

  const handleSerialNoSelect = (selectedSerial) => {
    setSerialNo(selectedSerial);
    setSearchSerialNo("");
    setShowSerialInput(false);
  };

  const handleManualInput = () => {
    setShowSerialInput(true);
    setSerialNo("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!serialNo.trim()) {
      setError("Seri numarası gereklidir");
      return;
    }

    try {
      await createEspressoDevice({
        token: auth.accessToken,
        serialNo: serialNo.trim(),
        cupNumber,
      }).unwrap();
      setCreated(true);
      setSuccess("Cihaz başarıyla oluşturuldu!");
      setError("");
    } catch (err) {
      // Error is handled in useEffect
    }
  };

  if (!mounted) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ease-in-out ${entered ? "opacity-100" : "opacity-0"}`}
        onClick={handleClose}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-screen w-full sm:w-[500px] lg:w-[600px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col ${
          entered ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-yellow-50 p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-xl">
                <FaCoffee className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800">
                  Espresso Cihaz Oluştur
                </h3>
                <p className="text-sm text-gray-600 mt-0.5">
                  Yeni espresso cihazı ekleyin
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-lg hover:bg-gray-100"
              aria-label="Drawer'ı kapat"
            >
              <FaTimes className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {error && (
              <Alert severity="error" className="mb-4">
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" className="mb-4">
                {success}
              </Alert>
            )}

            <form id="create-device-form" onSubmit={handleSubmit} className="space-y-4">
              {/* Serial Number Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Seri Numarası <span className="text-red-500">*</span>
                </label>

                {!showSerialInput ? (
                  <>
                    {/* Search/Select Mode */}
                    <div className="space-y-3">
                      <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          value={searchSerialNo}
                          onChange={(e) => setSearchSerialNo(e.target.value)}
                          placeholder="Seri numarası ara..."
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all"
                          disabled={isLoading || created}
                        />
                      </div>

                      {/* Selected Serial Number Display */}
                      {serialNo && (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Seçilen Seri Numarası</p>
                              <p className="text-base font-semibold text-gray-800">{serialNo}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setSerialNo("")}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <FaTimes className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Manual Input Button */}
                      <button
                        type="button"
                        onClick={handleManualInput}
                        className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-amber-500 hover:text-amber-600 transition-all flex items-center justify-center gap-2"
                        disabled={isLoading || created}
                      >
                        <FaPlus className="w-4 h-4" />
                        <span>Manuel Seri Numarası Gir</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Manual Input Mode */}
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={serialNo}
                        onChange={(e) => setSerialNo(e.target.value)}
                        placeholder="Örn: ESP-001"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all"
                        disabled={isLoading || created}
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setShowSerialInput(false);
                          setSerialNo("");
                        }}
                        className="w-full px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                        disabled={isLoading || created}
                      >
                        ← Geri Dön
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Cup Number Select */}
              <div>
                <label
                  htmlFor="cupNumber"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Fincan Sayısı
                </label>
                <select
                  id="cupNumber"
                  value={cupNumber}
                  onChange={(e) => setCupNumber(Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all"
                  disabled={isLoading || created}
                >
                  <option value={1}>1 Fincan</option>
                  <option value={2}>2 Fincan</option>
                  <option value={4}>4 Fincan</option>
                  <option value={6}>6 Fincan</option>
                </select>
              </div>

              {/* Products Info */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cihaz Ürünleri
                </label>
                <div className="grid grid-cols-2 gap-2 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  {ESPRESSO_PRODUCTS.map((product, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 text-sm text-gray-700"
                    >
                      <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                      <span>{product}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Bu ürün otomatik olarak cihaza eklenecektir.
                </p>
              </div>

            </form>
          </div>

          {/* Footer - Fixed at bottom */}
          <div className="flex-shrink-0 p-4 sm:p-6 bg-white border-t border-gray-200">
            <div className="flex gap-3">
              {created ? (
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-3 bg-emerald-500 text-white font-medium rounded-xl hover:bg-emerald-600 transition-colors"
                >
                  Kapat
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isLoading}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    form="create-device-form"
                    disabled={isLoading || !serialNo.trim()}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-600 to-orange-600 text-white font-medium rounded-xl hover:from-amber-700 hover:to-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Oluşturuluyor...</span>
                      </>
                    ) : (
                      <>
                        <FaPlus className="w-4 h-4" />
                        <span>Cihaz Oluştur</span>
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
    </>
  );
}
